import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Users, 
  Edit, 
  Plus,
  Trash2, 
  Crown, 
  Calendar, 
  Bot, 
  UserCheck, 
  AlertCircle,
  RefreshCw,
  Search
} from 'lucide-react'
import { adminManagementService, Admin, CreateAdminRequest, UpdateAdminRequest, UpdateSubscriptionRequest, Plan } from '../services/adminManagementService'
import { subscriptionPlanService } from '../services/subscriptionPlanService'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

// Função utilitária para formatar preço
const formatPrice = (price: any): string => {
  if (!price) return '0.00'
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2)
}

// Função para limpar o telefone (remover formatação)
const cleanPhoneNumber = (phone: string): string => {
  if (!phone) return ''
  // Remove todos os caracteres não numéricos
  return phone.replace(/\D/g, '')
}

// Função para formatar o telefone para exibição (opcional)
const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return ''
  const cleaned = cleanPhoneNumber(phone)
  // Se tem 13 dígitos (55 + 2 + 9), formata como +55 (11) 99999-9999
  if (cleaned.length === 13) {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`
  }
  // Se tem 11 dígitos (2 + 9), formata como (11) 99999-9999
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
  }
  // Se tem 10 dígitos (2 + 8), formata como (11) 9999-9999
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`
  }
  return cleaned
}

const AdminManagement: React.FC = () => {
  const navigate = useNavigate()
  const [admins, setAdmins] = useState<Admin[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [formData, setFormData] = useState<CreateAdminRequest>({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: '',
    plan_type: 'basic'
  })


  const planConfig: Record<string, { name: string; color: string; maxBots: number; maxAttendants: number; features: string[] }> = {
    basic: {
      name: 'Básico',
      color: 'bg-gray-500',
      maxBots: 1,
      maxAttendants: 3,
      features: ['Chatbot básico', '3 atendentes']
    },
    professional: {
      name: 'Profissional',
      color: 'bg-blue-500',
      maxBots: 3,
      maxAttendants: 10,
      features: ['Múltiplos chatbots', '10 atendentes', 'Templates', 'Analytics']
    },
    enterprise: {
      name: 'Empresarial',
      color: 'bg-purple-500',
      maxBots: 10,
      maxAttendants: 50,
      features: ['Chatbots ilimitados', '50 atendentes', 'API', 'Webhooks', 'Suporte prioritário']
    }
  }

  const loadAdmins = async () => {
    try {
      setLoading(true)
      const response = await adminManagementService.getAdminsWithSubscriptions()
      setAdmins(response.admins || [])
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 })
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar admins')
      setAdmins([]) // Garantir que admins seja sempre um array
    } finally {
      setLoading(false)
    }
  }

  const loadPlans = async () => {
    try {
      await subscriptionPlanService.getActivePlans()
      // Plans serão carregados individualmente nos modais
    } catch (err: any) {
      logger.error('Erro ao carregar planos:', err)
    }
  }

  useEffect(() => {
    loadAdmins()
    loadPlans()
  }, [])

  // Debug para modal de edição
  useEffect(() => {
    if (showEditModal && selectedAdmin) {
      logger.debug('🔍 Modal de edição aberto - selectedAdmin:', selectedAdmin)
      logger.debug('🔍 Modal de edição aberto - formData:', formData)
    }
  }, [showEditModal, selectedAdmin, formData])


  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Limpar o telefone antes de enviar
      const cleanedFormData = {
        ...formData,
        phone: cleanPhoneNumber(formData.phone || '')
      }
      await adminManagementService.createAdmin(cleanedFormData)
      toast.success('Administrador criado com sucesso!')
      setShowCreateModal(false)
      setFormData({
        name: '',
        email: '',
        password: '',
        company_name: '',
        phone: '',
        plan_type: 'basic'
      })
      loadAdmins()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao criar admin'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleUpdateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedAdmin) return

    try {
      const updateData: UpdateAdminRequest = {
        name: formData.name,
        email: formData.email,
        company_name: formData.company_name,
        phone: cleanPhoneNumber(formData.phone || '') // Limpar o telefone antes de enviar
      }
      await adminManagementService.updateAdmin(selectedAdmin.id, updateData)
      toast.success('Administrador atualizado com sucesso!')
      setShowEditModal(false)
      setSelectedAdmin(null)
      loadAdmins()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar admin'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleDeleteAdmin = async (id: number) => {
    if (!confirm('Tem certeza que deseja deletar este admin?')) return

    try {
      await adminManagementService.deleteAdmin(id)
      toast.success('Administrador deletado com sucesso!')
      loadAdmins()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao deletar admin'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleUpdateSubscription = async (subscriptionData: UpdateSubscriptionRequest) => {
    if (!selectedAdmin) return

    try {
      logger.debug('🔄 Atualizando assinatura:', subscriptionData)
      const result = await adminManagementService.updateAdminSubscription(selectedAdmin.id, subscriptionData)
      logger.debug('✅ Assinatura atualizada:', result)
      toast.success('Assinatura atualizada com sucesso!')
      setShowSubscriptionModal(false)
      setSelectedAdmin(null)
      await loadAdmins()
      logger.debug('✅ Dados recarregados')
    } catch (err: any) {
      logger.error('❌ Erro ao atualizar assinatura:', err)
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar assinatura'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'blocked':
        return 'bg-red-100 text-red-800'
      case 'expired':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (planType: string) => {
    return planConfig[planType as keyof typeof planConfig]?.color || 'bg-gray-500'
  }

  const filteredAdmins = (admins || []).filter(admin =>
    admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Carregando admins...</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Gerenciamento de Admins
              </h1>
              <p className="text-gray-600">Gerencie administradores e suas assinaturas</p>
            </div>
          </div>
          <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Novo Plano</span>
            </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => loadAdmins()}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Lista de Admins */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limites
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiração
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAdmins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                        <div className="text-sm text-gray-500">{admin.email}</div>
                        {admin.company_name && (
                          <div className="text-xs text-gray-400">{admin.company_name}</div>
                        )}
                        {admin.phone && (
                          <div className="text-xs text-gray-400">{formatPhoneForDisplay(admin.phone)}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {admin.subscription && admin.plan ? (
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getPlanColor(admin.plan.plan_type)}`}>
                          {planConfig[admin.plan.plan_type]?.name || admin.plan.plan_type}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Sem assinatura</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {admin.subscription ? (
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <Bot className="h-3 w-3 mr-1 text-gray-400" />
                          <span>Plano {admin.plan?.plan_type || 'N/A'}</span>
                        </div>
                        <div className="flex items-center">
                          <UserCheck className="h-3 w-3 mr-1 text-gray-400" />
                          <span>Status: {admin.subscription.status}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(admin.status)}`}>
                        {admin.status === 'active' ? 'Ativo' : 'Bloqueado'}
                      </span>
                      {admin.subscription && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(admin.subscription.status)}`}>
                          {admin.subscription.status === 'active' ? 'Assinatura Ativa' : 
                           admin.subscription.status === 'expired' ? 'Expirada' : 'Cancelada'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {admin.subscription ? (
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                        {new Date(admin.subscription.expiration_date).toLocaleDateString('pt-BR')}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          logger.debug('🔍 Dados do admin selecionado:', admin)
                          setSelectedAdmin(admin)
                          const formDataToSet = {
                            name: admin.name,
                            email: admin.email,
                            password: '',
                            company_name: admin.company_name || '',
                            phone: admin.phone || '',
                            plan_type: admin.plan?.plan_type || 'basic'
                          }
                          logger.debug('🔍 FormData a ser definido:', formDataToSet)
                          setFormData(formDataToSet)
                          setShowEditModal(true)
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin)
                          setShowSubscriptionModal(true)
                        }}
                        className="text-purple-600 hover:text-purple-900"
                        title="Gerenciar Assinatura"
                      >
                        <Crown className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAdmin(admin.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => loadAdmins()}
                disabled={pagination.page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => loadAdmins()}
                disabled={pagination.page >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> até{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  de <span className="font-medium">{pagination.total}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => loadAdmins()}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadAdmins()}
                    disabled={pagination.page >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Criar Admin */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Criar Novo Admin</h3>
              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Senha</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresa</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefone</label>
                  <input
                    type="text"
                    value={formatPhoneForDisplay(formData.phone || '')}
                    onChange={(e) => {
                      const cleaned = cleanPhoneNumber(e.target.value)
                      setFormData({ ...formData, phone: cleaned })
                    }}
                    placeholder="+55 11 99999-9999"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Será salvo como: {formData.phone || 'apenas números'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Plano</label>
                  <select
                    value={formData.plan_type}
                    onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as any })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="basic">Básico</option>
                    <option value="professional">Profissional</option>
                    <option value="enterprise">Empresarial</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-md btn-outline"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-md btn-primary">
                    Criar Admin
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Admin */}
      {showEditModal && selectedAdmin && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Admin</h3>
              <form onSubmit={handleUpdateAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresa</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefone</label>
                  <input
                    type="text"
                    value={formatPhoneForDisplay(formData.phone || '')}
                    onChange={(e) => {
                      const cleaned = cleanPhoneNumber(e.target.value)
                      setFormData({ ...formData, phone: cleaned })
                    }}
                    placeholder="+55 11 99999-9999"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Será salvo como: {formData.phone || 'apenas números'}
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="btn btn-md btn-outline"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-md btn-primary">
                    Atualizar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gerenciar Assinatura */}
      {showSubscriptionModal && selectedAdmin && (
        <SubscriptionModal
          admin={selectedAdmin}
          onClose={() => {
            setShowSubscriptionModal(false)
            setSelectedAdmin(null)
          }}
          onUpdate={handleUpdateSubscription}
        />
      )}
      </div>
    </div>
  )
}

// Componente Modal de Assinatura
interface SubscriptionModalProps {
  admin: Admin
  onClose: () => void
  onUpdate: (data: UpdateSubscriptionRequest) => void
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ admin, onClose, onUpdate }) => {
  const [plans, setPlans] = useState<Plan[]>([])
  const [formData, setFormData] = useState<UpdateSubscriptionRequest>({
    plan_id: admin.plan?.id || 1,
    plan_type: admin.plan?.plan_type || 'basic',
    expiration_date: admin.subscription?.expiration_date ? 
      new Date(admin.subscription.expiration_date).toISOString().split('T')[0] : 
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: admin.subscription?.status || 'active'
  })

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await subscriptionPlanService.getActivePlans()
        setPlans(response)
      } catch (err) {
        logger.error('Erro ao carregar planos:', err)
      }
    }
    loadPlans()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onUpdate(formData)
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Gerenciar Assinatura - {admin.name}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Plano</label>
              <select
                value={formData.plan_type}
                onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as any })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.plan_type}>
                    {plan.name} - R$ {formatPrice(plan.price)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data de Expiração</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Ativo</option>
                <option value="expired">Expirado</option>
                <option value="canceled">Cancelado</option>
              </select>
            </div>

            {/* Preview do Plano Selecionado */}
            {(() => {
              const selectedPlan = plans.find(p => p.plan_type === formData.plan_type)
              if (!selectedPlan) return null
              
              return (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Preview do Plano</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Bots:</span>
                      <span className="font-medium">{selectedPlan.max_bots}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Atendentes:</span>
                      <span className="font-medium">{selectedPlan.max_attendants}</span>
                    </div>
                    {selectedPlan.price && (
                      <div className="flex justify-between">
                        <span>Preço:</span>
                        <span className="font-medium">R$ {formatPrice(selectedPlan.price)}/mês</span>
                      </div>
                    )}
                    {selectedPlan.features && (() => {
                      try {
                        const featuresObj = typeof selectedPlan.features === 'string' 
                          ? JSON.parse(selectedPlan.features) 
                          : selectedPlan.features;
                        
                        if (typeof featuresObj === 'object' && featuresObj !== null && Object.keys(featuresObj).length > 0) {
                          return (
                            <div>
                              <span className="font-medium">Recursos:</span>
                              <ul className="mt-1 space-y-1">
                                {Object.entries(featuresObj).map(([feature, enabled]) => (
                                  <li key={feature} className="flex items-center">
                                    <span className={`w-2 h-2 rounded-full mr-2 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                                    <span className={enabled ? 'text-gray-900' : 'text-gray-400'}>
                                      {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      } catch (error) {
                        logger.error('Erro ao processar features:', error);
                        return null;
                      }
                    })()}
                  </div>
                </div>
              )
            })()}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-md btn-outline"
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-md btn-primary">
                Atualizar Assinatura
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AdminManagement
